import React, { useCallback, useState } from "react";
import { graphql } from "react-relay";

import * as Sentry from "@sentry/browser";
import cx from "classnames";
import { camelCase } from "lodash";

import { commitMutationPromise, useLazyLoadQuery } from "~/lib/relayUtil";
import { Fallbacks } from "~/lib/suspense";
import { environment } from "~/models";
import { ObjectivesPage } from "~/stream/StreamSettings/Objectives";
import { HelpCenterIcon, Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/LoadingSpinner";

import type { TeamSettingsMutation } from "~/__generated__/TeamSettingsMutation.graphql";
import type { TeamSettingsQuery } from "~/__generated__/TeamSettingsQuery.graphql";

import stylesStreamSettings from "~/stream/StreamSettings/StreamSettings.module.css";

export type TeamSettingsResponseTeam = Extract<TeamSettingsQuery["response"]["team"], { __typename: "Team" }>;

type SectionKey = "objectives";

const setSettingMutation = graphql`
	mutation TeamSettingsMutation(
		$id: ID!
		$objectivesFilterAssignee: JSON
		$objectivesFilterStatus: String
		$objectivesFilterCycle: JSON
	) {
		updateTeam(
			id: $id
			objectivesFilterAssignee: $objectivesFilterAssignee
			objectivesFilterStatus: $objectivesFilterStatus
			objectivesFilterCycle: $objectivesFilterCycle
		) {
			team {
				objectivesFilterAssignee
				objectivesFilterStatus
				objectivesFilterCycle
			}
		}
	}
`;

function SectionNavButton({
	section,
	setter,
	current,
	children,
}: {
	section: SectionKey;
	setter: (key: SectionKey) => void;
	current: SectionKey;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={useCallback(() => setter(section), [setter, section])}
			className={cx(stylesStreamSettings.sectionNavButton, { [stylesStreamSettings.current]: section === current })}
		>
			{children}
		</button>
	);
}

function ErrorFallback() {
	React.useEffect(() => {
		Sentry.captureException(new Error("TeamSettingsModal could not find Team"));
	}, []);

	return null;
}

function LoadingFallback() {
	return <LoadingSpinner dimmer delay={1000} />;
}

interface Props {
	close: () => void;
	teamId: string;
}

function TeamSettingsModal({ close, teamId }: Props): React.ReactElement {
	// the default null value makes TS type `props` as potentially null, which it otherwise wouldn't, but should
	const props = useLazyLoadQuery<TeamSettingsQuery>(
		graphql`
			query TeamSettingsQuery($teamId: ID!) {
				team: node(id: $teamId) {
					... on Team {
						__typename
						id
						objectivesFilterAssignee
						objectivesFilterStatus
						objectivesFilterCycle
					}
				}
			}
		`,
		{ teamId },
		{ fetchPolicy: "network-only" },
	);

	const team = props?.team?.__typename === "Team" ? props.team : null;

	const [section, setSection] = useState<SectionKey>("objectives");

	const setTeamSetting = useCallback((teamId: string, setting: string) => {
		return (value: string | null) => () => {
			const adjustedSetting = camelCase(setting);

			const variables = {
				id: teamId,
				[adjustedSetting]: value,
			};

			if (adjustedSetting === "objectivesFilterStatus" && value === null) {
				variables[adjustedSetting] = "";
			}

			commitMutationPromise<TeamSettingsMutation>(environment, {
				mutation: setSettingMutation,
				variables,
				onCompleted: (payload, err) => {
					if (err?.length || !payload) throw new Error("Failed to save settings");
				},
			});
		};
	}, []);

	return (
		<Modal className={stylesStreamSettings.modal} onClose={close} data-test-id="team-settings-modal">
			<Modal.Header className={stylesStreamSettings.modalHeader}>
				<Modal.Title>Team Settings</Modal.Title>
				<Modal.HeaderActions onClose={close}>
					<HelpCenterIcon
						articleId="5672806"
						title="Objectives and teams"
						className={stylesStreamSettings.headerButton}
					/>
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body className={stylesStreamSettings.modalBody}>
				<nav className={stylesStreamSettings.sectionNav}>
					<SectionNavButton section="objectives" setter={setSection} current={section}>
						Objectives
					</SectionNavButton>
				</nav>
				<div
					className={cx(stylesStreamSettings.sectionBody, {
						[stylesStreamSettings.sectionBody]: section === "objectives",
					})}
				>
					{!team ? (
						"Error: Could not find team. It may have been deleted. Please try again later."
					) : (
						<ObjectivesPage data={team} setSetting={setTeamSetting} />
					)}
				</div>
			</Modal.Body>
		</Modal>
	);
}

export default function TeamSettingsModalWrapper(props: Props) {
	return (
		<Fallbacks loading={<LoadingFallback />} error={<ErrorFallback />}>
			<TeamSettingsModal {...props} />
		</Fallbacks>
	);
}
